import React, { useState, useEffect, useRef } from "react";
import { PlaybackOptions } from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import styles from "../../../styles/ChordProgression.module.css";
import { Note } from "../../../soundLibrary";
import { ROUTES } from "../../../config/routes";
import { formatChordProgression } from "../../../utils/musicTheory";

export type ChordProgressionQuizOptions = Omit<ChordProgressionQuizProps, "playbackOptions" | "currentProgression" | "setCurrentProgression" | "questionCount" | "setQuestionCount" | "attemptedQuestions" | "setAttemptedQuestions" | "score" | "setScore" | "timeLeft" | "setTimeLeft" | "feedback" | "setFeedback" | "generateQuestion" | "quizEnded" | "setQuizEnded" | "resetQuiz" | "playReferenceNote" | "stopAll" | "abortPlaybackRef">;

export interface ChordProgressionQuizProps {
    selectedChords: number[];
    progressionLength: number;
    keyNote: Note;
    mode: "questions" | "time" | "endless";
    questionLimit?: number;
    timeLimitSeconds?: number;
    difficulty?: number;
    playbackOptions: PlaybackOptions;
    currentProgression?: {
        progressionNotes: Note[][];
        correctProgression: number[];
        questionOptions: number[][];
    } | null;
    setCurrentProgression?: (progression: any) => void;
    questionCount?: number;
    setQuestionCount?: (count: number) => void;
    attemptedQuestions?: number;
    setAttemptedQuestions?: (count: number) => void;
    score?: number;
    setScore?: (score: number) => void;
    timeLeft?: number;
    setTimeLeft?: (time: number) => void;
    feedback?: { message: string; color: string } | null;
    setFeedback?: (feedback: any) => void;
    generateQuestion?: () => void;
    quizEnded?: boolean;
    setQuizEnded?: (ended: boolean) => void;
    resetQuiz?: () => void;
    playReferenceNote?: () => void;
    stopAll?: () => void;
    abortPlaybackRef?: React.MutableRefObject<boolean>;
}

export default function ChordProgressionQuiz({
    selectedChords,
    progressionLength,
    keyNote,
    mode,
    questionLimit,
    timeLimitSeconds,
    difficulty = 1,
    playbackOptions,
    currentProgression,
    setCurrentProgression,
    questionCount = 0,
    setQuestionCount,
    attemptedQuestions = 0,
    setAttemptedQuestions,
    score = 0,
    setScore,
    timeLeft = 0,
    setTimeLeft,
    feedback,
    setFeedback,
    generateQuestion,
    quizEnded = false,
    setQuizEnded,
    resetQuiz,
    playReferenceNote,
    stopAll,
    abortPlaybackRef,
}: ChordProgressionQuizProps) {
    const [disabledOptions, setDisabledOptions] = useState<number[][]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<number[] | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Clear any pending timeouts when quiz ends
    useEffect(() => {
        if (quizEnded && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [quizEnded]);

    // Reset correctAnswer and disabledOptions when question changes
    useEffect(() => {
        setCorrectAnswer(null);
        setDisabledOptions([]);
    }, [currentProgression]);

    // Auto-end quiz when timer runs out
    useEffect(() => {
        if (mode === "time" && timeLeft === 0 && !quizEnded && setQuizEnded) {
            if (stopAll) stopAll(); // Stop any currently playing progression
            if (abortPlaybackRef) abortPlaybackRef.current = true;
            setQuizEnded(true);
        }
    }, [mode, timeLeft, quizEnded, setQuizEnded, stopAll, abortPlaybackRef]);

    // Scroll to top when completion screen shows
    useEffect(() => {
        if (quizEnded || (mode === "questions" && questionLimit && questionCount >= questionLimit)) {
            // Scroll all possible containers
            // 1. The quiz wrapper itself
            if (containerRef.current) {
                containerRef.current.scrollTop = 0;
            }
            // 2. Find and scroll all parent scrollable containers
            let element = containerRef.current?.parentElement;
            while (element) {
                const overflow = window.getComputedStyle(element).overflowY;
                if (overflow === 'auto' || overflow === 'scroll') {
                    element.scrollTop = 0;
                }
                element = element.parentElement;
            }
            // 3. Scroll window as final backup
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded, mode, questionLimit, questionCount]);

    const handleAnswer = (answerProgression: number[]) => {
        if (!currentProgression || !setFeedback || !setScore || !setQuestionCount || !setCurrentProgression) return;

        // Skip if already disabled
        if (disabledOptions.some((prog) => arraysEqual(prog, answerProgression))) {
            return;
        }

        // Increment attempted questions only on the first answer attempt
        if (disabledOptions.length === 0 && setAttemptedQuestions) {
            setAttemptedQuestions(attemptedQuestions + 1);
        }

        const isCorrect = arraysEqual(answerProgression, currentProgression.correctProgression);

        if (isCorrect) {
            if (disabledOptions.length === 0) {
                setScore(score + 1);
            }
            
            // ✅ Correct feedback
            setFeedback({ message: "✓ Correct!", color: "#22c55e" });
            setCorrectAnswer(answerProgression);

            // Increment question count immediately when answer is selected
            if (setQuestionCount) {
                setQuestionCount(questionCount + 1);
            }

            // Check if quiz should end after this question
            const shouldEndQuiz = (mode === "questions" && questionLimit && (questionCount + 1) >= questionLimit) || 
                                 quizEnded;

            // Advance after 2s, but only if quiz isn't ending
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setFeedback(null);
                setDisabledOptions([]);
                setCorrectAnswer(null);
                if (!shouldEndQuiz && generateQuestion && !quizEnded) {
                    generateQuestion();
                } else if (shouldEndQuiz && !quizEnded) {
                    // End the quiz when the final question is answered
                    if (stopAll) stopAll();
                    if (abortPlaybackRef) abortPlaybackRef.current = true;
                    if (setQuizEnded) {
                        setQuizEnded(true);
                    }
                }
                timeoutRef.current = null;
            }, 2000);
        } else {
            // ❌ Wrong feedback
            setDisabledOptions((prev) => [...prev, answerProgression]);
            setFeedback({ message: "Try again!", color: "#ef4444" });
        }
    };

    // Show completion screen if quiz ended manually OR question limit reached
    if (quizEnded || (mode === "questions" && questionLimit && questionCount >= questionLimit)) {
        const percentage = attemptedQuestions > 0 ? Math.round((score / attemptedQuestions) * 100) : 0;
        const needsMorePractice = percentage < 70;
        return (
            <div className={styles.quizCompletedPanel}>
                <div className={styles.completionIcon}>🎉</div>
                <h2 className={styles.completionTitle}>Quiz Complete!</h2>
                <div className={styles.scoreDisplay}>
                    <div className={styles.scoreNumber}>{score}/{attemptedQuestions}</div>
                    <div className={styles.scorePercentage}>{percentage}%</div>
                </div>
                <div className={styles.completionMessage}>
                    You answered {score} of {attemptedQuestions} questions correctly
                </div>
                {resetQuiz && (
                    <button onClick={resetQuiz} className={styles.newQuizButton}>
                        New Quiz
                    </button>
                )}
                <div className={styles.completionLearnMore}>
                    <LearnMoreBanner
                        to={ROUTES.LEARN.PROGRESSIONS}
                        variant="completion"
                        title={needsMorePractice ? "Want to improve?" : "Keep learning"}
                        description={needsMorePractice
                            ? "Learn how chord progressions work"
                            : "Explore common progression patterns"
                        }
                    />
                </div>
            </div>
        );
    }

    if (mode === "time" && timeLeft <= 0) {
        const percentage = questionCount > 0 ? Math.round((score / questionCount) * 100) : 0;
        const needsMorePractice = percentage < 70;
        return (
            <div className={styles.quizCompletedPanel}>
                <div className={styles.completionIcon}>⏰</div>
                <h2 className={styles.completionTitle}>Time's Up!</h2>
                <div className={styles.scoreDisplay}>
                    <div className={styles.scoreNumber}>{score}/{questionCount}</div>
                    <div className={styles.scorePercentage}>{percentage}%</div>
                </div>
                <div className={styles.completionMessage}>
                    You answered {score} of {questionCount} questions correctly
                </div>
                {resetQuiz && (
                    <button onClick={resetQuiz} className={styles.newQuizButton}>
                        New Quiz
                    </button>
                )}
                <div className={styles.completionLearnMore}>
                    <LearnMoreBanner
                        to={ROUTES.LEARN.PROGRESSIONS}
                        variant="completion"
                        title={needsMorePractice ? "Want to improve?" : "Keep learning"}
                        description={needsMorePractice
                            ? "Learn how chord progressions work"
                            : "Explore common progression patterns"
                        }
                    />
                </div>
            </div>
        );
    }

    if (!currentProgression) {
        return <div className={styles.loadingMessage}>Loading question...</div>;
    }

    return (
        <div ref={containerRef} className={styles.chordsQuizWrapper}>
            <div className={styles.questionPrompt}>
                <p>Which chord progression do you hear?</p>
                <p className={styles.referenceNote}>
                    Key: <strong>{keyNote}</strong>
                    {playReferenceNote && (
                        <button 
                            onClick={playReferenceNote} 
                            className={styles.playReferenceButton}
                            title="Play reference note"
                        >
                            ♪
                        </button>
                    )}
                </p>
            </div>

            <div className={styles.answerOptionsPanel}>
                <div className={styles.questionOptsWrapper}>
                    {currentProgression.questionOptions.map((progression, index) => (
                        <button
                            key={`${keyNote}-${progressionLength}-${index}`}
                            onClick={() => handleAnswer(progression)}
                            disabled={(correctAnswer !== null && !arraysEqual(correctAnswer, progression)) || disabledOptions.some((prog) => arraysEqual(prog, progression))}
                            className={
                                correctAnswer && arraysEqual(correctAnswer, progression)
                                    ? `${styles.chordOptButton} ${styles.chordOptButtonCorrect}`
                                    : disabledOptions.some((prog) => arraysEqual(prog, progression))
                                    ? `${styles.chordOptButton} ${styles.chordOptButtonDisabled}`
                                    : styles.chordOptButton
                            }
                        >
                            {formatChordProgression(progression)}
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.feedbackContainer}>
                {feedback && (
                    <span style={{ color: feedback.color }}>
                        {feedback.message}
                    </span>
                )}
            </div>
        </div>
    );
}

function arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
}
